import React from "react";

import {
  Button,
  Card,
  CardContent,
  Container,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router";

import { AppBreadcrumbs } from "@shell/components/AppBreadcrumbs";
import { getContainerStyles } from "@shell/styles/themeUtils";
import {
  ORGANIZATIONS,
  PLATFORM_PRODUCTS,
} from "src/platform/constants/routes";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

import CustomerIdentityManagementPage from "./CustomerIdentityManagementPage";
import LlmGatewayManagementPage from "./LlmGatewayManagementPage";

export type ProductDetailsViewState =
  "loaded" | "loading" | "error" | "forbidden";

const LLM_GATEWAY = {
  key: "llm-gateway",
  name: "LLM Gateway",
  category: "AI infrastructure",
  description:
    "A managed, organization-scoped gateway for routing application requests to approved language-model backends.",
  capabilities: [
    "OpenAI-compatible request shape",
    "Organization-scoped routing",
    "Usage visibility",
  ],
};
const CUSTOMER_IDENTITY_KEY = "customer-identity";

const StateCard: React.FC<{
  title: string;
  onBack: () => void;
  children: React.ReactNode;
}> = ({ title, onBack, children }) => (
  <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
    <CardContent>
      <Stack
        spacing={2}
        alignItems="center"
        sx={{ textAlign: "center", py: 4 }}
      >
        <Typography variant="h5" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" maxWidth={480}>
          {children}
        </Typography>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{ textTransform: "none" }}
        >
          Back to product catalog
        </Button>
      </Stack>
    </CardContent>
  </Card>
);

export interface PlatformProductDetailsPageProps {
  /** Optional deterministic state hook for route and state coverage. */
  viewState?: ProductDetailsViewState;
}

const PlatformProductDetailsPage: React.FC<PlatformProductDetailsPageProps> = ({
  viewState = "loaded",
}) => {
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias, productKey } = useParams<{
    orgAlias: string;
    productKey: string;
  }>();

  if (!orgAlias || !productKey) return null;

  const backToCatalog = () => appNavigation.goToPlatformProducts(orgAlias);
  const breadcrumbs = (
    <AppBreadcrumbs
      items={[
        { label: "Organizations", to: ORGANIZATIONS.LIST.path },
        { label: orgAlias, to: ORGANIZATIONS.DETAILS.getPath(orgAlias) },
        {
          label: PLATFORM_PRODUCTS.CATALOG.title,
          to: PLATFORM_PRODUCTS.CATALOG.getPath(orgAlias),
        },
        { label: "Product details" },
      ]}
    />
  );

  if (viewState === "loading") {
    return (
      <Container sx={getContainerStyles(theme)}>
        {breadcrumbs}
        <Stack
          spacing={2}
          sx={{ mt: 3 }}
          role="status"
          aria-label="Loading product details"
        >
          <Skeleton variant="text" width={280} height={42} />
          <Skeleton variant="text" width={560} height={24} />
          <Skeleton variant="rounded" height={220} />
        </Stack>
      </Container>
    );
  }

  if (viewState === "error") {
    return (
      <Container sx={getContainerStyles(theme)}>
        {breadcrumbs}
        <StateCard title="Product details unavailable" onBack={backToCatalog}>
          Product details could not be loaded. Refresh the page to try again.
        </StateCard>
      </Container>
    );
  }

  if (viewState === "forbidden") {
    return (
      <Container sx={getContainerStyles(theme)}>
        {breadcrumbs}
        <StateCard title="Permission required" onBack={backToCatalog}>
          You do not have permission to view product details for this
          organization.
        </StateCard>
      </Container>
    );
  }

  if (productKey === CUSTOMER_IDENTITY_KEY) {
    return <CustomerIdentityManagementPage />;
  }

  if (productKey !== LLM_GATEWAY.key) {
    return (
      <Container sx={getContainerStyles(theme)}>
        {breadcrumbs}
        <StateCard title="Product details unavailable" onBack={backToCatalog}>
          This product does not have a read-only details destination in the
          current release.
        </StateCard>
      </Container>
    );
  }

  return (
    <Container sx={getContainerStyles(theme)}>
      {breadcrumbs}
      <LlmGatewayManagementPage
        orgAlias={orgAlias}
        viewState={viewState}
        onBack={backToCatalog}
      />
    </Container>
  );
};

export default PlatformProductDetailsPage;
